import { useTranslation } from 'react-i18next';
import { useReports } from '@/hooks/useReports';
import '@/styles/reports.css';

type Tone = 'primary' | 'success' | 'warning' | 'secondary' | 'alert';

interface KpiDef {
  labelKey: string;
  value: string;
  sub?: string;
  tone: Tone;
  icon: string;
  change?: number;
}

function fmtMoney(n: number): string {
  if (n === 0) return '0';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtPct(n: number): string {
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

export function ReportsRoute() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useReports();

  if (isLoading) return <div className="reports-page" aria-busy="true" />;

  if (error || !data) {
    return (
      <div className="reports-page">
        <div className="reports-empty">
          <span className="material-symbols-outlined">error</span>
          {t('reports.load_error')}
        </div>
      </div>
    );
  }

  const { bi, po } = data;

  const cards: KpiDef[] = [
    {
      labelKey: 'reports.kpi_revenue',
      value: fmtMoney(bi.revenue),
      tone: 'primary',
      icon: 'trending_up',
      change: bi.revenueChange,
    },
    {
      labelKey: 'reports.kpi_profit',
      value: fmtMoney(bi.profit),
      tone: 'success',
      icon: 'savings',
      change: bi.profitChange,
    },
    {
      labelKey: 'reports.kpi_cash',
      value: fmtMoney(bi.cashBalance),
      tone: 'secondary',
      icon: 'account_balance_wallet',
    },
    {
      labelKey: 'reports.kpi_ar',
      value: fmtMoney(bi.arBalance),
      sub: t('reports.sub_invoices', { count: bi.arCount }),
      tone: 'warning',
      icon: 'request_page',
    },
    {
      labelKey: 'reports.kpi_ap',
      value: fmtMoney(bi.apBalance),
      sub: t('reports.sub_invoices', { count: bi.apCount }),
      tone: 'alert',
      icon: 'payments',
    },
    {
      labelKey: 'reports.kpi_open_po',
      value: String(po.draft + po.confirmed + po.partialReceived),
      sub: t('reports.sub_po_monthly', { amount: fmtMoney(po.monthlyAmount) }),
      tone: 'secondary',
      icon: 'shopping_cart',
    },
  ];

  return (
    <div className="reports-page">
      <div className="reports-period">
        <span className="material-symbols-outlined">calendar_month</span>
        {bi.period}
      </div>
      <div className="reports-grid">
        {cards.map((c) => (
          <div key={c.labelKey} className="reports-card" data-tone={c.tone}>
            <div className="reports-card-head">
              <div className="reports-card-icon">
                <span className="material-symbols-outlined">{c.icon}</span>
              </div>
              <div className="reports-card-label">{t(c.labelKey)}</div>
            </div>
            <div className="reports-card-value">{c.value}</div>
            {c.sub && <div className="reports-card-sub">{c.sub}</div>}
            {typeof c.change === 'number' && c.change !== 0 && (
              <div className={`reports-card-change ${c.change > 0 ? 'up' : 'down'}`}>
                <span className="material-symbols-outlined">
                  {c.change > 0 ? 'trending_up' : 'trending_down'}
                </span>
                {fmtPct(c.change)}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="reports-po-bar">
        <div className="reports-po-bar-title">{t('reports.po_breakdown')}</div>
        <div className="reports-po-chips">
          <div className="reports-po-chip" data-kind="draft">
            <span>{po.draft}</span>
            <small>{t('reports.po_draft')}</small>
          </div>
          <div className="reports-po-chip" data-kind="confirmed">
            <span>{po.confirmed}</span>
            <small>{t('reports.po_confirmed')}</small>
          </div>
          <div className="reports-po-chip" data-kind="partial">
            <span>{po.partialReceived}</span>
            <small>{t('reports.po_partial')}</small>
          </div>
          <div className="reports-po-chip" data-kind="received">
            <span>{po.received}</span>
            <small>{t('reports.po_received')}</small>
          </div>
        </div>
      </div>
    </div>
  );
}
